import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';
import { getClassLevelFromStream, getClassData } from '../data/academicDatabase.js';

export default function TextbookAnswers() {
  const navigate = useNavigate();
  const { theme, user } = useAuthStore();
  const isICSE = theme === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [classData, setClassData] = useState(null);
  const [showClassPrompt, setShowClassPrompt] = useState(false);

  // Get dynamic class data based on user's stream
  useEffect(() => {
    if (user?.stream) {
      const classLevel = getClassLevelFromStream(user.stream);
      const data = getClassData(classLevel);
      setClassData(data);
      setShowClassPrompt(false);
      
      // Reset selections when class changes
      setSelectedSubject(null);
      setSelectedBook(null);
      setSelectedChapter(null);
      setSelectedExercise(null);
    } else {
      setShowClassPrompt(true);
    }
  }, [user?.stream]);

  const textbookData = classData?.textbookAnswers || [];
  const selectedSubjectData = textbookData.find(item => item.subjectId === selectedSubject);
  const books = selectedSubjectData ? selectedSubjectData.books : [];
  const chapters = selectedBook ? selectedBook.chapters : [];
  const exercises = selectedChapter ? selectedChapter.exercises : [];
  const questions = selectedExercise ? selectedExercise.questions : [];

  const handleBack = () => {
    if (selectedExercise) {
      setSelectedExercise(null);
    } else if (selectedChapter) {
      setSelectedChapter(null);
    } else if (selectedBook) {
      setSelectedBook(null);
    } else if (selectedSubject) {
      setSelectedSubject(null);
    } else {
      navigate('/');
    }
  };

  const renderSubjects = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {textbookData.map((subjectData, index) => (
        <motion.div
          key={subjectData.subjectId}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
          onClick={() => setSelectedSubject(subjectData.subjectId)}
          className="glass-card rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-all border border-white/10"
        >
          <div className="text-4xl mb-4">{getSubjectIcon(subjectData.subjectId)}</div>
          <h3 className="text-xl font-bold text-white mb-2 capitalize">{subjectData.subjectName}</h3>
          <p className="text-gray-400 text-sm">{subjectData.books.length} textbooks available</p>
          <div className={`mt-4 text-${primaryColor}-400 font-medium flex items-center gap-2`}>
            <span>Browse Books</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  const renderBooks = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      {books.map((book, index) => (
        <motion.div
          key={book.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
          onClick={() => setSelectedBook(book)}
          className="glass-card rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-all border border-white/10"
        >
          <div className="text-3xl mb-4">📖</div>
          <h3 className="text-xl font-bold text-white mb-2">{book.name}</h3>
          <p className="text-gray-400 text-sm">{book.chapters.length} chapters</p>
          <div className={`mt-4 text-${primaryColor}-400 font-medium flex items-center gap-2`}>
            <span>View Chapters</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  const renderChapters = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {chapters.map((chapter, index) => (
        <motion.div
          key={chapter.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => setSelectedChapter(chapter)}
          className="glass-card rounded-xl p-4 cursor-pointer hover:scale-[1.02] transition-all border border-white/10"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold`}>
              {index + 1}
            </div>
            <div>
              <h4 className="text-white font-medium">{chapter.name}</h4>
              <p className="text-gray-400 text-sm">{chapter.exercises.length} exercises</p>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  const renderExercises = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-4"
    >
      {exercises.map((exercise, index) => (
        <motion.div
          key={exercise.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={() => setSelectedExercise(exercise)}
          className="glass-card rounded-xl p-4 cursor-pointer hover:scale-[1.01] transition-all border border-white/10"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold`}>
                {index + 1}
              </div>
              <div>
                <h4 className="text-white font-medium">{exercise.name}</h4>
                <p className="text-gray-400 text-sm">{exercise.questions.length} questions</p>
              </div>
            </div>
            <svg className={`w-5 h-5 text-${primaryColor}-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  const renderQuestions = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {questions.map((question, index) => (
        <motion.div
          key={question.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="glass-card rounded-2xl p-6 border border-white/10"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              Q{index + 1}
            </div>
            <div className="flex-1">
              <h4 className="text-white font-medium mb-2">{question.questionText}</h4>
              <div className="text-gray-300 text-sm leading-relaxed">
                <div className={`text-${primaryColor}-400 font-medium mb-2`}>Solution:</div>
                <div dangerouslySetInnerHTML={{ __html: question.solution }} />
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  const getSubjectIcon = (subject) => {
    const icons = {
      maths: '🔢',
      physics: '⚛️',
      chemistry: '🧪',
      biology: '🧬',
      history: '📜',
      geography: '🌍',
      computers: '💻',
    };
    return icons[subject] || '📚';
  };

  const getBreadcrumb = () => {
    const items = ['Textbooks'];
    if (selectedSubject && selectedSubjectData) items.push(selectedSubjectData.subjectName);
    if (selectedBook) items.push(selectedBook.name);
    if (selectedChapter) items.push(selectedChapter.name);
    if (selectedExercise) items.push(selectedExercise.name);
    return items.join(' > ');
  };

  // Render class selection prompt for users without class
  const renderClassPrompt = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto text-center"
    >
      <div className="glass-card rounded-3xl p-12 border border-white/10">
        <div className="text-6xl mb-6">📚</div>
        <h2 className="text-3xl font-bold text-white mb-4">Select Your Class</h2>
        <p className="text-gray-400 mb-8">
          Please select your class in your profile to view relevant textbook solutions and resources.
        </p>
        <button
          onClick={() => navigate('/profile')}
          className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
        >
          Go to Profile Settings
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className={`w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Textbook Answers</h1>
              <p className={`text-${primaryColor}-400 text-sm`}>{classData?.className || getBreadcrumb()}</p>
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Back to Portal
          </button>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {showClassPrompt && renderClassPrompt()}
            {!showClassPrompt && !selectedSubject && renderSubjects()}
            {!showClassPrompt && selectedSubject && !selectedBook && renderBooks()}
            {!showClassPrompt && selectedBook && !selectedChapter && renderChapters()}
            {!showClassPrompt && selectedChapter && !selectedExercise && renderExercises()}
            {!showClassPrompt && selectedExercise && renderQuestions()}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
